import type { Book, Chapter, LibrarySource, ProgressFile, Track } from '../types';
import { EMPTY_PROGRESS } from '../types';

/**
  * Demo library — a LibrarySource backed by the generated files in
  * public/demo-audio, deliberately mirroring the two layouts the real scanner
  * has to cope with: a folder of per-chapter files, and a single long file with
  * embedded chapter marks.
  *
  * It exists so the whole app — library, player, chapter logic, sleep timer and
  * the cross-device progress merge — can be run and tested without a Google
  * account. Its "remote" progress lives in localStorage under a distinct key,
  * standing in for the appDataFolder file, so the sync/merge path is exercised
  * for real rather than short-circuited.
  */

const REMOTE_KEY = 'shelf.demo.remoteProgress';

function demoUrl(file: string): string {
    return `${import.meta.env.BASE_URL}demo-audio/${file}`;
}

/** Byte size of an 8-bit 8 kHz mono WAV of the given length, matching what
  * scripts/make-demo-audio.mjs writes. */
const wavBytes = (seconds: number) => Math.round(seconds * 8000) + 44;

function track(id: string, name: string, file: string, seconds: number): Track {
    return {
       id,
       name,
       fileName: file,
       mimeType: 'audio/wav',
       sizeBytes: wavBytes(seconds),
       durationSec: seconds,
    };
}

/** A soft gradient cover, inline so demo mode needs no image assets. */
function cover(title: string, author: string, from: string, to: string): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
    <defs>
       <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
         <stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>
       </linearGradient>
       <radialGradient id="v" cx="0.3" cy="0.2" r="0.9">
         <stop offset="0" stop-color="#fff" stop-opacity="0.28"/>
         <stop offset="1" stop-color="#fff" stop-opacity="0"/>
       </radialGradient>
    </defs>
    <rect width="400" height="400" fill="url(#g)"/>
    <rect width="400" height="400" fill="url(#v)"/>
    <text x="200" y="196" text-anchor="middle" fill="#fff" font-family="Segoe UI, Inter, sans-serif"
           font-size="34" font-weight="700" opacity="0.96">${title}</text>
    <text x="200" y="232" text-anchor="middle" fill="#fff" font-family="Segoe UI, Inter, sans-serif"
           font-size="19" font-weight="500" opacity="0.75">${author}</text>
</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const BOOKS: Book[] = [
    {
    id: 'demo-willows',
    title: 'The Wind in the Willows',
    author: 'Kenneth Grahame',
    kind: 'folder',
    coverUrl: cover('Willows', 'Kenneth Grahame', '#4c6ef5', '#7048e8'),
    tracks: [
       track('demo-willows-1', 'The River Bank', 'willows-01.wav', 42),
       track('demo-willows-2', 'The Open Road', 'willows-02.wav', 58),
       track('demo-willows-3', 'The Wild Wood', 'willows-03.wav', 36),
    ],
    totalSizeBytes: wavBytes(42 + 58 + 36),
  },
  {
    id: 'demo-meditations',
    title: 'Meditations',
    author: 'Marcus Aurelius',
    kind: 'single',
    coverUrl: cover('Meditations', 'Marcus Aurelius', '#0b7285', '#1098ad'),
    tracks: [track('demo-meditations-1', 'Meditations', 'meditations.wav', 180)],
    totalSizeBytes: wavBytes(180),
  },
  {
    // No cover on purpose: exercises the generated-initials fallback tile.
    id: 'demo-notes',
    title: 'Notes on Nothing in Particular',
    author: 'Anonymous',
    kind: 'single',
    tracks: [track('demo-notes-1', 'Notes on Nothing in Particular', 'willows-03.wav', 36)],
    totalSizeBytes: wavBytes(36),
   },
];

/** Chapter marks for the single-file book, as a real .m4b parse would yield. */
const CHAPTERS: Record<string, Chapter[]> = {
   'demo-meditations': [
      { title: 'Book I — Debts and Lessons', trackIndex: 0, startSec: 0 },
      { title: 'Book II — On the River Gran', trackIndex: 0, startSec: 45 },
      { title: 'Book III — In Carnuntum', trackIndex: 0, startSec: 90 },
      { title: 'Book IV — The Inner Citadel', trackIndex: 0, startSec: 135 },
   ],
};

export class DemoSource implements LibrarySource {
   readonly kind = 'demo' as const;
   readonly label = 'Demo library';

   async listBooks(): Promise<Book[]> {
      // A beat of latency so loading states are visible and testable.
      await new Promise((r) => setTimeout(r, 180));
      return BOOKS.map((b) => ({ ...b, tracks: b.tracks.map((t) => ({ ...t })) }));
   }

   trackUrl(track: Track): string {
      return demoUrl(track.fileName);
   }

   async chaptersFor(book: Book): Promise<Chapter[]> {
      const explicit = CHAPTERS[book.id];
      if (explicit) return explicit;
      // Multi-file books get one chapter per file, which is what the real
      // scanner does when a book has no embedded chapter data.
      return book.tracks.map((t, i) => ({ title: t.name, trackIndex: i, startSec: 0 }));
   }

   async loadProgress(): Promise<ProgressFile | null> {
      await new Promise((r) => setTimeout(r, 120));
      const raw = localStorage.getItem(REMOTE_KEY);
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as ProgressFile;
        return parsed?.books ? parsed : EMPTY_PROGRESS;
      } catch {
        return EMPTY_PROGRESS;
      }
   }

   async saveProgress(progress: ProgressFile): Promise<void> {
      await new Promise((r) => setTimeout(r, 80));
      localStorage.setItem(REMOTE_KEY, JSON.stringify(progress));
   }
}
